<template>
  <v-container fluid class="modulvalidasi-page pa-4">
    <PageHeader
      icon="mdi-shield-check"
      title="Modul Validasi (Pamer Data)"
      subtitle="Pengurusan dan semakan validasi data"
    />
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="snackbar.timeout">
      {{ snackbar.message }}
    </v-snackbar>

    <!-- Export scope warning. Without BOTH Negeri and Pusingan the export spans
         every batch of the year — tens of thousands of HIDs, each with its kuiri
         recomputed server-side — so it is worth a deliberate confirmation rather
         than letting a stray click start a very long job. -->
    <v-dialog v-model="exportConfirm.show" max-width="460" persistent>
      <v-card>
        <v-card-title class="text-subtitle-1 warning--text text--darken-3">
          <v-icon left color="warning darken-3">mdi-alert</v-icon>
          Eksport tanpa penapis penuh
        </v-card-title>
        <v-card-text class="pb-2">
          <p class="mb-2">
            Penapis berikut belum dipilih:
            <b>{{ exportMissingScope.join(" dan ") }}</b>.
          </p>
          <p class="mb-2">
            Eksport {{ exportConfirm.label }} akan merangkumi
            <b>{{ fmtNum(listTotal) }}</b> baris dan dijangka mengambil masa
            <b>~{{ fmtClock(exportConfirm.eta) }}</b>.
          </p>
          <p class="mb-0 grey--text text--darken-1 caption">
            Pilih Negeri dan Pusingan dahulu untuk fail yang lebih kecil dan pantas.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text small @click="exportConfirm.show = false">Batal</v-btn>
          <v-btn color="primary" small depressed @click="confirmExport">Teruskan</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Sejarah Semak Semula — past runs, newest first. -->
    <v-dialog v-model="historyDialog" max-width="1000" scrollable>
      <v-card>
        <v-card-title class="text-subtitle-1">
          <v-icon left>mdi-history</v-icon> Sejarah Semak Semula
          <v-spacer></v-spacer>
          <v-btn icon small :loading="historyLoading" title="Muat semula" @click="loadHistory">
            <v-icon>mdi-reload</v-icon>
          </v-btn>
          <v-btn icon small title="Tutup" @click="historyDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-0" style="max-height:70vh">
          <div v-if="historyLoading && !historyRows.length" class="text-center pa-6">
            <v-progress-circular indeterminate size="26"></v-progress-circular>
          </div>
          <div v-else-if="!historyRows.length" class="text-center pa-6 grey--text">
            Tiada rekod semakan lagi.
          </div>
          <v-simple-table v-else dense class="hist-table">
            <template v-slot:default>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Negeri</th>
                  <th>Pusingan</th>
                  <th>Oleh</th>
                  <th>Mula</th>
                  <th class="text-right">Tempoh</th>
                  <th class="text-right">Batch</th>
                  <th class="text-right">HID</th>
                  <th class="text-center">Status</th>
                  <th class="text-center">Ralat</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="j in historyRows" :key="j.id">
                  <td class="grey--text">{{ j.id }}</td>
                  <!-- NULL scope means the run covered everything, which is the
                       single most useful thing to see at a glance here. -->
                  <td :class="{ 'grey--text': !j.filter_ng }">{{ j.filter_ng ? negeriLabel(j.filter_ng) : 'Semua negeri' }}</td>
                  <td :class="{ 'grey--text': !j.filter_p }">{{ j.filter_p ? ('P' + j.filter_p) : 'Semua pusingan' }}</td>
                  <td :class="{ 'grey--text': !j.triggered_by_name }">{{ j.triggered_by_name || '—' }}</td>
                  <td>{{ fmtDate(j.started_at) }}</td>
                  <td class="text-right">{{ j.duration_seconds == null ? '—' : fmtClock(j.duration_seconds) }}</td>
                  <td class="text-right">{{ fmtNum(j.batches_done) }}<span class="grey--text">/{{ fmtNum(j.batches_total) }}</span></td>
                  <td class="text-right">{{ fmtNum(j.hids_done) }}</td>
                  <td class="text-center">
                    <v-chip x-small label :color="jobStatusStyle(j.status).bg" :class="jobStatusStyle(j.status).text"
                      class="font-weight-medium">{{ jobStatusLabel(j.status) }}</v-chip>
                  </td>
                  <td class="text-center">
                    <v-btn v-if="j.error_message" icon x-small color="red darken-1"
                      title="Lihat ralat (JSON)" @click="showJobError(j)">
                      <v-icon small>mdi-alert-circle</v-icon>
                    </v-btn>
                    <span v-else class="grey--text">—</span>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Raw JSON for one failed run, opened from the Ralat icon above. -->
    <v-dialog v-model="errorDialog.show" max-width="820" scrollable>
      <v-card>
        <v-card-title class="text-subtitle-1 red--text text--darken-2">
          <v-icon left color="red darken-2">mdi-alert-circle</v-icon>
          Ralat semakan #{{ errorDialog.job && errorDialog.job.id }}
          <v-spacer></v-spacer>
          <v-btn icon small title="Salin JSON" @click="copyErrorJson"><v-icon>mdi-content-copy</v-icon></v-btn>
          <v-btn icon small title="Tutup" @click="errorDialog.show = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-0" style="max-height:70vh">
          <pre class="err-json">{{ errorJson }}</pre>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Kod Kuiri detail — mini popup version of the Rujukan Kriteria row,
         opened by clicking a Kod Kuiri inside an expanded HID. -->
    <v-dialog v-model="kuiriInfo.show" max-width="640" scrollable>
      <v-card v-if="kuiriInfo.data">
        <v-card-title class="text-subtitle-1 kuiri-info__title">
          <v-chip :color="sevColor(kuiriInfo.data.severity)" dark small label class="mr-2 kuiri-info__code">{{ kuiriInfo.data.code }}</v-chip>
          <span class="font-weight-medium">{{ kuiriInfo.data.jenisRekod || '—' }}</span>
          <v-spacer></v-spacer>
          <v-btn icon small title="Tutup" @click="kuiriInfo.show = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-0">
          <v-simple-table dense class="kuiri-info__table">
            <template v-slot:default>
              <tbody>
                <tr>
                  <th>Keluarga</th>
                  <td>{{ kuiriInfo.data.family }}<span v-if="kuiriInfo.familyName"> — {{ kuiriInfo.familyName }}</span></td>
                </tr>
                <tr>
                  <th>Keterukan</th>
                  <td><v-chip :color="sevColor(kuiriInfo.data.severity)" dark x-small label>{{ kuiriInfo.data.severityLabel || sevLabel(kuiriInfo.data.severity) }}</v-chip></td>
                </tr>
                <tr>
                  <th>Status Enjin</th>
                  <td>
                    <v-chip v-if="kuiriInfo.data.closed" color="red darken-1" dark x-small label>Tutup</v-chip>
                    <v-chip v-else-if="kuiriInfo.data.active" color="green darken-1" dark x-small label>Aktif</v-chip>
                    <v-chip v-else color="grey lighten-1" dark x-small label>Nyahaktif</v-chip>
                  </td>
                </tr>
                <tr v-if="kuiriInfo.data.vars && kuiriInfo.data.vars.length">
                  <th>Pembolehubah</th>
                  <td><v-chip v-for="c in kuiriInfo.data.vars" :key="c" class="ma-1" x-small outlined>{{ c }}</v-chip></td>
                </tr>
                <tr>
                  <th>Semakan (Panduan)</th>
                  <td>
                    {{ kuiriInfo.data.semakan || '—' }}
                    <div v-if="kuiriInfo.data.formula" class="grey--text text-caption">Σ {{ kuiriInfo.data.formula }}</div>
                    <div v-if="kuiriInfo.data.lampiran" class="grey--text text-caption">Lampiran: {{ kuiriInfo.data.lampiran }}</div>
                  </td>
                </tr>
                <tr>
                  <th>Logik Enjin</th>
                  <td><span class="kuiri-info__logik">{{ kuiriInfo.data.logik || '—' }}</span></td>
                </tr>
                <tr>
                  <th>Tindakan</th>
                  <td>{{ kuiriInfo.data.tindakan || '—' }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text small :to="{ name: 'ModulvalidasiRujukan' }">
            <v-icon left small>mdi-book-open-variant</v-icon> Buka Rujukan Kriteria
          </v-btn>
        </v-card-actions>
      </v-card>
      <v-card v-else>
        <v-card-text class="text-center pa-6 grey--text">
          Butiran kuiri <b>{{ kuiriInfo.code }}</b> tidak dijumpai.
          <div class="mt-3"><v-btn text small @click="kuiriInfo.show = false">Tutup</v-btn></div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- header + filters + recheck -->
    <v-card class="mb-4" outlined>
      <v-card-title class="py-3 d-flex align-center flex-wrap kad__tajuk">
        <v-icon small class="mr-2">mdi-shield-check</v-icon>
        <span class="subtitle-1 font-weight-medium">Semak Semula Validasi</span>
        <v-spacer></v-spacer>
        <v-btn outlined class="reset-btn text-none mr-2" :to="{ name: 'ModulvalidasiRujukan' }">
          <v-icon left>mdi-book-open-variant</v-icon> Rujukan Kriteria
        </v-btn>
        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn class="btn-teal text-none" :loading="job && job.status === 'running'" v-bind="attrs" v-on="on">
              <v-icon left>mdi-refresh</v-icon> Semak Semula
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item @click="recheck('all')"><v-list-item-title>Semua Negeri &amp; Pusingan (P01–P12)</v-list-item-title></v-list-item>
            <v-list-item :disabled="!filterNegeri" @click="recheck('negeri')"><v-list-item-title>Negeri {{ filterNegeri || '—' }} sahaja</v-list-item-title></v-list-item>
            <v-list-item :disabled="!filterPusingan" @click="recheck('pusingan')"><v-list-item-title>Pusingan {{ filterPusingan || '—' }} sahaja</v-list-item-title></v-list-item>
            <v-list-item :disabled="!filterNegeri || !filterPusingan" @click="recheck('batch')"><v-list-item-title>Batch ini (NG{{ filterNegeri }}/P{{ filterPusingan }})</v-list-item-title></v-list-item>
            <v-divider></v-divider>
            <v-list-item @click="openHistory">
              <v-list-item-title><v-icon small left>mdi-history</v-icon> Sejarah</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-card-title>
      <v-card-text class="filter-bar py-3">
        <v-row align="end" dense>
          <!-- No Tahun filter: one survey cycle is pusingan 01→12 and straddles
               two calendar years, so a year filter would split it. -->
          <v-col cols="12" sm="3">
            <div class="fld__label">Negeri</div>
            <v-select v-model="filterNegeri" :items="negeriOptions" outlined dense hide-details clearable item-text="text" item-value="value" @change="onFilter"></v-select>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="fld__label">Pusingan</div>
            <v-select v-model="filterPusingan" :items="pusinganOptions" outlined dense hide-details clearable @change="onFilter"></v-select>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="12" sm="auto">
            <v-btn outlined height="40" class="text-none reset-btn" @click="resetFilters">
              <v-icon small left>mdi-refresh</v-icon>
              Tetapkan Semula
            </v-btn>
          </v-col>
        </v-row>
        <v-alert v-if="job && job.status === 'running'" type="info" dense outlined class="mt-3 mb-0">
          <div class="d-flex align-center flex-wrap" style="gap:4px 10px">
            <span>
              Semakan sedang berjalan… batch {{ job.batches_done }}/{{ job.batches_total }}
              ({{ fmtNum(job.hids_done) }}<template v-if="job.hids_total"> / {{ fmtNum(job.hids_total) }}</template> HID)
            </span>
            <v-chip x-small label color="info" outlined>{{ jobPct }}%</v-chip>
            <v-spacer></v-spacer>
            <span class="caption">
              <template v-if="job.eta_seconds != null">
                Anggaran siap dalam <b>{{ fmtDur(job.eta_seconds) }}</b>
                <span class="grey--text"> (~{{ etaClock }})</span>
              </template>
              <template v-else>Menganggarkan masa…</template>
              <span class="grey--text" v-if="job.elapsed_seconds != null"> · berjalan {{ fmtDur(job.elapsed_seconds) }}</span>
            </span>
          </div>
          <v-progress-linear :value="jobPct" height="6" class="mt-1"></v-progress-linear>
          <!-- failures accumulate live while the job is still running -->
          <div v-if="job.error_message" class="mt-2 caption red--text text--darken-1"
               style="white-space:pre-line;max-height:160px;overflow-y:auto">{{ job.error_message }}</div>
        </v-alert>
        <v-alert v-else-if="job && job.status === 'failed'" type="error" dense outlined class="mt-3 mb-0">
          <div class="font-weight-medium">Semakan terakhir gagal</div>
          <div class="mt-1 caption" style="white-space:pre-line;max-height:240px;overflow-y:auto">{{ job.error_message }}</div>
        </v-alert>
        <v-alert v-if="needsSchema" type="warning" dense outlined class="mt-3 mb-0">
          Jadual hasil belum wujud. Jalankan <code>schema/modul-validasi-hasil.sql</code> dahulu, kemudian tekan Semak Semula.
        </v-alert>
      </v-card-text>
    </v-card>

    <!-- summary matrix (display only) -->
    <v-card outlined class="mb-4">
      <v-card-title class="py-3 d-flex align-center flex-wrap kad__tajuk">
        <v-icon small class="mr-2">mdi-chart-bar</v-icon>
        <span class="subtitle-1 font-weight-medium">Status Validasi — Negeri × Pusingan</span>
        <v-spacer></v-spacer>
        <v-btn icon small :loading="loading" title="Muat semula" @click="loadSummary">
          <v-icon>mdi-reload</v-icon>
        </v-btn>
        <!-- Collapsed keeps only the JUMLAH NEGERI SELESAI row; expanded shows
             the full Negeri × Pusingan matrix with smooth transition. -->
        <v-btn icon small :title="summaryOpen ? 'Kecilkan — papar Jumlah Negeri Selesai sahaja' : 'Kembangkan — papar semua negeri'"
          @click="summaryOpen = !summaryOpen">
          <v-icon :class="{ 'rotate-180': summaryOpen }" class="chevron-anim">mdi-chevron-down</v-icon>
        </v-btn>
      </v-card-title>
      <!-- No fixed height / own scrollbar: collapsed by default (just the
           JUMLAH NEGERI SELESAI row) so it doesn't eat page space, and when
           expanded it renders fully and the page scrolls past it. -->
      <v-simple-table dense class="summary-table-head">
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left" style="min-width:160px">Negeri</th>
              <th v-for="p in pusingans" :key="p" class="text-center">P{{ p }}</th>
            </tr>
            <tr class="grey lighten-3">
              <td class="font-weight-bold">JUMLAH NEGERI SELESAI</td>
              <td v-for="p in pusingans" :key="p" class="text-center font-weight-bold primary--text">{{ doneByPusingan[p] || 0 }} / {{ negeris.length }}</td>
            </tr>
          </thead>
        </template>
      </v-simple-table>
      <v-expand-transition>
        <div v-show="summaryOpen" class="summary-table-body-wrapper">
          <v-simple-table dense class="summary-table-body">
            <template v-slot:default>
              <tbody>
                <tr v-for="ng in negeris" :key="ng.value">
                  <td style="min-width:160px">{{ ng.value }} - {{ ng.name }}</td>
                  <td v-for="p in pusingans" :key="p" class="text-center">
                    <template v-if="cell(ng.value, p)">
                      <!-- Hovering the icon breaks the batch down; a cell with
                           pending data changes is badged regardless of its status. -->
                      <v-tooltip bottom color="grey darken-4">
                        <template v-slot:activator="{ on, attrs }">
                          <span v-bind="attrs" v-on="on" class="cell-hit">
                            <v-badge :value="cell(ng.value, p).perubahan > 0" color="deep-purple"
                              icon="mdi-database-edit" overlap bordered offset-x="6" offset-y="6">
                              <v-icon v-if="cell(ng.value, p).checked === 0" color="grey lighten-1" small>mdi-minus-circle-outline</v-icon>
                              <v-icon v-else-if="cell(ng.value, p).allClean" color="success" small>mdi-check-circle</v-icon>
                              <span v-else>
                                <v-icon :color="cell(ng.value, p).tahan > 0 ? 'red darken-1' : 'orange darken-2'" small>{{ cell(ng.value, p).tahan > 0 ? 'mdi-close-circle' : 'mdi-alert' }}</v-icon>
                                <!-- the batch size (Semua), not the tahan+semak tally -->
                                <span class="caption ml-1">{{ fmtNum(cell(ng.value, p).checked) }}</span>
                              </span>
                            </v-badge>
                          </span>
                        </template>
                        <div class="cell-tip">
                          <div class="cell-tip__head">{{ ng.value }} - {{ ng.name }} · Pusingan {{ p }}</div>
                          <table>
                            <tr><td>Semua</td><td>{{ fmtNum(cell(ng.value, p).checked) }}</td></tr>
                            <tr><td>Tahan</td><td class="red--text text--lighten-2">{{ fmtNum(cell(ng.value, p).tahan) }}</td></tr>
                            <tr><td>Semak</td><td class="orange--text text--lighten-2">{{ fmtNum(cell(ng.value, p).semak) }}</td></tr>
                            <tr><td>Lulus</td><td class="green--text text--lighten-2">{{ fmtNum(cell(ng.value, p).boleh) }}</td></tr>
                            <tr><td>Perubahan Data</td>
                              <td :class="cell(ng.value, p).perubahan > 0 ? 'deep-purple--text text--lighten-3' : ''">
                                {{ fmtNum(cell(ng.value, p).perubahan) }}
                              </td></tr>
                          </table>
                          <div class="cell-tip__foot">
                            Terakhir Dijana: {{ cell(ng.value, p).generatedAt ? fmtDate(cell(ng.value, p).generatedAt) : '—' }}
                          </div>
                        </div>
                      </v-tooltip>
                    </template>
                    <span v-else class="grey--text">—</span>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>
      </v-expand-transition>
      <!-- Bottom affordance mirroring the header chevron: sits right under
           JUMLAH NEGERI SELESAI (the last row when collapsed) so the invite to
           expand is where the user's eye already is, not just up in the title. -->
      <div class="summary-expand-hint" @click="summaryOpen = !summaryOpen">
        <span>{{ summaryOpen ? 'Kecilkan Info Negeri' : 'Kembangkan Info Negeri' }}</span>
        <v-icon small color="primary" :class="{ 'rotate-180': summaryOpen }" class="chevron-anim">mdi-chevron-down</v-icon>
      </div>
    </v-card>

    <!-- listing (below the summary) -->
    <v-card outlined>
      <v-card-title class="py-3 d-flex flex-wrap align-center kad__tajuk" style="gap:8px">
        <div class="d-flex flex-column" style="min-width:0">
          <div class="d-flex align-center">
            <v-icon small class="mr-2">mdi-format-list-bulleted</v-icon>
            <span class="subtitle-1 font-weight-medium">Senarai HID</span>
          </div>
          <!-- The in-header search box is too narrow to read a long query back,
               so echo the parsed terms here. Showing them SPLIT also confirms
               how the input was interpreted. -->
          <span v-if="hidTerms.length" class="hid-echo" :title="hidTerms.join(', ')">
            Carian HID: <b>{{ hidTerms.join(', ') }}</b>
            <span v-if="hidTerms.length > 1" class="grey--text">({{ hidTerms.length }} HID)</span>
            <v-icon x-small class="ml-1 hid-echo-clear" title="Kosongkan carian" @click="onHidClear">mdi-close-circle</v-icon>
          </span>
        </div>
        <!-- Both formats are built on the SERVER and stream back as a finished
             file. They render every row expanded, and the kuiri are recomputed
             per HID, so a large export takes a while — hence the readout. -->
        <!-- ONE spinner for the whole export area. Independent CSS animations
             each start at their own t=0, so a spinner per button plus one per
             status line drifted out of phase and read as several unrelated
             things happening. The per-format bars below are determinate, so
             they convey progress without spinning. -->
        <v-spacer></v-spacer>
        <!-- Same line as the title, right end; the progress readout rides along
             at their left. Same button and icon sizing as the Pendapatan Purata
             pages.
             Disabled rather than :loading — a button spinner would be another
             out-of-phase animation next to the status block's one. -->
        <div class="d-flex align-center export-btns">
          <div v-if="runningExports.length" class="export-status mr-2">
            <div class="d-flex align-center">
              <v-progress-circular indeterminate size="14" width="2" color="primary" class="mr-2"></v-progress-circular>
              <span class="font-weight-medium">Menjana {{ runningExports.map(j => j.label).join(" & ") }}…</span>
            </div>
            <div v-for="j in runningExports" :key="j.kind" class="export-job">
              <span class="grey--text">{{ j.label }}</span>
              <span class="ml-1">{{ fmtClock(j.elapsed) }} / ~{{ fmtClock(j.eta) }}</span>
              <v-progress-linear :value="Math.min(99, (j.elapsed / Math.max(1, j.eta)) * 100)"
                height="3" rounded color="primary"></v-progress-linear>
            </div>
          </div>
          <v-btn color="green darken-2" :dark="!exporting && !!listTotal" class="mr-2 text-none"
            :disabled="exporting || !listTotal" title="Eksport Excel (semua baris dikembangkan)"
            @click="exportExcel">
            <v-icon left>mdi-microsoft-excel</v-icon>
            Excel
          </v-btn>
          <v-btn color="red darken-1" :dark="!exportingPdf && !!listTotal" class="text-none"
            :disabled="exportingPdf || !listTotal" title="Eksport PDF (semua baris dikembangkan)"
            @click="exportPDF">
            <v-icon left>mdi-file-pdf-box</v-icon>
            PDF
          </v-btn>
        </div>
      </v-card-title>
      <!-- Same filter band as the Semak Semula card above: labelled columns on
           the teal strip, so both cards read as one filter language. -->
      <v-card-text class="filter-bar py-3">
        <v-row align="end" dense>
          <!-- Keluarga (kuiri family) — the same five Buku Panduan families the
               Rujukan page filters on, same "SJ — Semakan Julat" labels. Keeps a
               HID only when at least one of its kuiri belongs to the chosen
               family, and composes with the Status toggle beside it — those
               counts follow this filter. -->
          <v-col cols="12" sm="4" md="3">
            <div class="fld__label">Jenis Kuiri</div>
            <v-select v-model="filterKeluarga" :items="keluargaOptions" class="keluarga-select"
              outlined dense hide-details clearable @change="onFilter"></v-select>
          </v-col>
          <v-col cols="12" sm="4" md="3">
            <div class="fld__label">Kod Kuiri</div>
            <v-autocomplete v-model="filterKodKuiri" :items="kuiriOptions" outlined dense hide-details clearable
              item-text="text" item-value="value" @change="onFilter"></v-autocomplete>
          </v-col>
          <v-spacer></v-spacer>
          <!-- Each bucket shows its size for the current Pusingan/Negeri
               scope; the counts ignore the active status filter so they stay
               readable as a breakdown while one bucket is selected.
               Colours are shared with the Status chips (see statusStyle) so the
               toggle doubles as the legend for the column. -->
          <v-col cols="12" sm="auto">
            <div class="fld__label">Status</div>
            <v-btn-toggle v-model="statusFilter" dense mandatory class="status-toggle" @change="onFilter">
              <v-tooltip bottom max-width="320">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn small value="" v-bind="attrs" v-on="on">
                    <span class="tg-label">Semua</span>
                    <span class="tg-count">{{ fmtNum(listCounts.ALL) }}</span>
                  </v-btn>
                </template>
                <span>Paparkan semua rekod HID tanpa mengira status.</span>
              </v-tooltip>
              <!-- Combined Tahan & Semak — shows both statuses at once, so the
                   user can view/export both without toggling twice. Flows into
                   the Excel/PDF/CSV exports like any other status filter. -->
              <v-tooltip bottom max-width="320">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn small :value="COMBINED" class="tg-combo" v-bind="attrs" v-on="on">
                    <span class="tg-label">Tahan &amp; Semak</span>
                    <span class="tg-count">{{ fmtNum(listCounts.TAHAN + listCounts.SEMAK) }}</span>
                  </v-btn>
                </template>
                <span>Paparkan HID berstatus Tahan ATAU Semak sekali gus — sesuai untuk memuat turun kedua-duanya.</span>
              </v-tooltip>
              <v-tooltip v-for="s in STATUSES" :key="s" bottom max-width="320">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn small :value="s" :color="statusStyle(s).bg" v-bind="attrs" v-on="on">
                    <span class="tg-label">{{ statusLabel(s) }}</span>
                    <span class="tg-count" :class="statusStyle(s).text">{{ fmtNum(listCounts[s]) }}</span>
                  </v-btn>
                </template>
                <span>{{ statusTooltip(s) }}</span>
              </v-tooltip>
            </v-btn-toggle>
          </v-col>
        </v-row>
      </v-card-text>
      <!-- The Keluarga of a HID is recorded when it is validated, so results
           stored before this filter existed carry none and can never match.
           Without this note that reads as "no such HID", not "not yet checked". -->
      <v-alert v-if="filterKeluarga && staleKeluarga" type="info" dense outlined class="mx-4 mb-2">
        {{ fmtNum(staleKeluarga) }} HID dalam skop ini belum disemak sejak penapis Jenis Kuiri
        ditambah, jadi jenis kuirinya belum direkod dan ia tidak muncul di bawah.
        Jalankan <b>Semak Semula</b> untuk memasukkannya.
      </v-alert>
      <!-- --hid-body-min reserves the full page-size height on the table body.
           This is what actually stops the page from jumping: on a search with NO
           matches Vuetify's genItems() bails at `if (empty) return [empty]` and
           renders ONLY the "tiada rekod" row — the padding rows in displayRows
           never get rendered — so the height has to be held from the outside. -->
      <v-data-table
        :headers="listHeaders"
        :items="displayRows"
        :server-items-length="listTotal"
        :loading="listLoading"
        :options.sync="options"
        @update:options="loadList"
        single-expand
        :expanded="expanded"
        @item-expanded="loadDetail"
        item-key="HID"
        dense
        class="hid-table"
        hide-default-header
        :style="{ '--hid-body-min': bodyMinHeight + 'px' }"
        :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
      >
        <!-- Two-row header: group titles on top, the columns themselves below.
             The whole thead is replaced (a per-column header slot cannot add a
             spanning row), so sorting is handled here — see toggleSort.
             `hide-default-header` is REQUIRED: genHeaders() renders this slot
             *and then* the default header row unless it is set, which showed up
             as a third header row duplicating the second. -->
        <template v-slot:header>
          <thead>
            <tr>
              <th rowspan="2" class="idx-col ident-col">#</th>
              <th
                v-for="g in headerGroups"
                :key="g.key"
                :colspan="g.span"
                :class="['grp-col', 'text-center', { 'ident-col': g.key === 'ident' }]"
              >{{ g.text }}</th>
            </tr>
            <tr>
              <th
                v-for="h in dataHeaders"
                :key="h.value"
                :class="['sub-col', h.align === 'center' ? 'text-center' : 'text-left', { sortable: h.sortable !== false, active: sortCol === h.value, 'ident-col': h.group === 'ident' }]"
                :style="h.width ? { width: h.width + 'px' } : null"
                @click="toggleSort(h)"
              >
                {{ h.text }}
                <v-icon v-if="h.sortable !== false" x-small class="sort-ico" :class="{ show: sortCol === h.value }">
                  {{ sortCol === h.value && sortDesc ? 'mdi-arrow-down' : 'mdi-arrow-up' }}
                </v-icon>
                <!-- In-header HID search — sits inline beside the label so the
                     header row keeps its original height. .native.stop keeps
                     clicks (and the clear button) off the th's sort handler. -->
                <v-text-field
                  v-if="h.value === 'HID'"
                  v-model="hidSearch"
                  class="hid-search"
                  dense solo flat hide-details clearable
                  placeholder="Cari…"
                  title="Cari satu atau lebih HID. Pisahkan dengan koma atau ruang — cth. hid1, hid2. Sebahagian HID juga boleh (cth. nombor batch)."
                  @click.native.stop
                  @input="onHidInput"
                  @click:clear="onHidClear"
                ></v-text-field>
              </th>
            </tr>
          </thead>
        </template>

        <!-- The whole row toggles the expansion, not just the chevron. -->
        <template v-slot:item="{ item, index }">
          <!-- Padding row: keeps the table at a constant itemsPerPage height so a
               narrow search result doesn't collapse the table and scroll the page
               up. Blank, non-interactive, same column widths as real rows. -->
          <tr v-if="item.__pad" :key="item.HID" class="hid-row hid-row--pad">
            <td class="idx-col"></td>
            <td class="pk-col"></td>
            <td v-for="h in dataHeaders.slice(1)" :key="h.value" :class="h.align === 'center' ? 'text-center' : ''"></td>
          </tr>
          <tr v-else :key="item.HID" class="hid-row" :class="{ 'is-open': isRowExpanded(item) }" @click="onRowClick(item)">
            <td class="idx-col">{{ rowNo(index) }}</td>
            <td class="pk-col" :title="item.HID">{{ item.HID }}</td>
            <td
              v-for="h in dataHeaders.slice(1)"
              :key="h.value"
              :class="[h.align === 'center' ? 'text-center' : '', { 'empty-cell': item[h.value] === '' || item[h.value] == null }]"
            >
              <!-- The expand toggle lives INSIDE the Status cell rather than in
                   a column of its own, so every table column maps 1:1 onto an
                   exported column (the chevron is UI-only and never exports). -->
              <template v-if="h.value === 'status'">
                <div class="status-cell">
                  <v-chip :color="statusStyle(item.status).bg" :class="statusStyle(item.status).text"
                    x-small label class="font-weight-medium">{{ statusLabel(item.status) }}</v-chip>
                  <!-- .stop: the row itself already toggles, without it the
                       chevron would fire twice and immediately re-collapse. -->
                  <v-btn icon x-small class="exp-btn" :title="isRowExpanded(item) ? 'Tutup kuiri' : 'Lihat kuiri'" @click.stop="toggleExpand(item)">
                    <v-icon small :class="{ 'rotate-180': isRowExpanded(item) }" class="chevron-anim">mdi-chevron-down</v-icon>
                  </v-btn>
                </div>
              </template>
              <template v-else>{{ item[h.value] }}</template>
            </td>
          </tr>
        </template>

        <!-- NOTE: when an `item` slot is present Vuetify takes the
             genScopedRows() path, which pushes this slot's output straight into
             the tbody WITHOUT wrapping it in a <tr> (unlike the default path).
             So this slot must render its own <tr> — a bare <td> is invalid
             markup and the browser silently drops it. -->
        <template v-slot:expanded-item="{ item }">
          <tr class="kuiri-row">
            <td :colspan="totalCols" class="pa-0">
              <v-expand-transition>
                <div v-show="isRowExpanded(item)" class="kuiri-expand-wrapper">
                  <div class="pa-3">
                    <v-fade-transition mode="out-in">
                      <div v-if="detailLoading[item.HID]" key="loading" class="text-center pa-2">
                        <v-progress-circular indeterminate size="22"></v-progress-circular>
                      </div>
                      <div v-else-if="detail[item.HID]" key="content">
                        <div v-if="detail[item.HID].errors.length === 0" class="green--text font-weight-medium">Tiada kuiri — Lulus.</div>
                        <v-simple-table v-else dense class="kuiri-table">
                          <template v-slot:default>
                            <!-- widths are required: table-layout:fixed otherwise
                                 splits the columns evenly and starves the text ones -->
                            <thead><tr>
                              <th style="width:8%">Kuiri</th>
                              <th style="width:9%">Keterukan</th>
                              <th style="width:13%">Pembolehubah</th>
                              <!-- Header is the raw field name (NOAIR); the info
                                   badge carries what it means. Rendered once per
                                   expanded HID, since this whole panel is per-HID. -->
                              <th style="width:10%">
                                NOAIR
                                <v-tooltip bottom>
                                  <template v-slot:activator="{ on, attrs }">
                                    <span v-bind="attrs" v-on="on" class="noair-badge">
                                      <v-icon x-small>mdi-information</v-icon>
                                    </span>
                                  </template>
                                  <span>Nombor Ahli isi Rumah</span>
                                </v-tooltip>
                              </th>
                              <th style="width:30%">Mesej</th>
                              <th style="width:30%">Penyelesaian</th>
                            </tr></thead>
                            <tbody>
                              <tr v-for="e in detail[item.HID].errors" :key="e.code + (e.members||[]).join()">
                                <!-- Clicking the Kod Kuiri opens its full Rujukan
                                     Kriteria detail in a mini popup. -->
                                <td>
                                  <a class="kod-link" @click.stop="openKuiriInfo(e.code)" :title="'Lihat butiran ' + e.code">{{ e.code }}</a>
                                </td>
                                <td><v-chip :color="sevColor(e.severity)" dark x-small label>{{ e.severityLabel }}</v-chip></td>
                                <td><v-chip v-for="c in e.columns" :key="c" class="ma-1" x-small outlined>{{ c }}</v-chip></td>
                                <td>{{ e.members ? e.members.join(', ') : '—' }}</td>
                                <!-- Message, then the actual values of the affected
                                     variables on their own line (item 5). -->
                                <td>
                                  <div v-html="e.message"></div>
                                  <div v-if="e.valuesText" class="kuiri-values">
                                    <span class="kuiri-values__lbl">Nilai pembolehubah:</span> {{ e.valuesText }}
                                  </div>
                                </td>
                                <td>{{ e.solution }}</td>
                              </tr>
                            </tbody>
                          </template>
                        </v-simple-table>
                      </div>
                    </v-fade-transition>
                  </div>
                </div>
              </v-expand-transition>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import axios from "../../plugins/axios";
import { fileStamp } from "../../services/fileStamp";
const NEGERI = [
  ["01", "Johor"], ["02", "Kedah"], ["03", "Kelantan"], ["04", "Melaka"],
  ["05", "N.Sembilan"], ["06", "Pahang"], ["07", "Pulau Pinang"], ["08", "Perak"],
  ["09", "Perlis"], ["10", "Selangor"], ["11", "Terengganu"], ["12", "Sabah"],
  ["13", "Sarawak"], ["14", "W.P. Kuala Lumpur"], ["15", "W.P. Labuan"], ["16", "W.P. Putrajaya"],
];
const PUSINGAN = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));

// Single source of truth for status colouring, shared by the filter toggle and
// the Status chips so the toggle reads as the column's legend. Pale background
// + dark text (rather than solid dark chips) keeps a dense, mostly-Tahan table
// readable instead of turning it into a wall of red.
// Text uses the DARKEST shade of each hue: the chips render at 10px on a pastel
// background, where the mid shades fall well under a readable contrast ratio.
// (deep-orange rather than orange for Semak — plain orange darken-4 on orange
// lighten-4 is the weakest pairing of the three.)
const STATUS_STYLE = {
  TAHAN: { bg: "red lighten-4", text: "red--text text--darken-4" },
  SEMAK: { bg: "orange lighten-4", text: "deep-orange--text text--darken-4" },
  "BOLEH HANTAR": { bg: "green lighten-4", text: "green--text text--darken-4" },
};

export default {
  name: "ModulValidasi",
  data() {
    return {
      // Scope is (Pusingan, Negeri) only — see the filter row in the template
      // for why there is no Tahun.
      filterPusingan: null,
      filterNegeri: null,
      // Kuiri family — filters the Senarai HID only, not the Negeri×Pusingan
      // matrix above it (same as the Status toggle).
      filterKeluarga: null,
      keluargaList: [],    // [{ code, name, rules, panduan }] from /modul-validasi/keluarga
      filterKodKuiri: null,
      kuiriList: [],       // [{ code, family, jenisRekod, semakan }] from /modul-validasi/kuiri
      staleKeluarga: 0,    // HIDs in scope not yet revalidated since the filter landed
      statusFilter: "",
      loading: false,
      summaryOpen: false,  // collapsed by default → only the JUMLAH NEGERI SELESAI row
      needsSchema: false,
      cells: {},
      job: null,
      pollTimer: null,
      // listing
      listRows: [], listTotal: 0, listLoading: false,
      listCounts: { ALL: 0, TAHAN: 0, SEMAK: 0, "BOLEH HANTAR": 0 },
      exporting: false,
      exportingPdf: false,
      exportJobs: {},   // { excel|pdf: { label, eta, elapsed } } — one per format
      // Pending export awaiting confirmation when Negeri/Pusingan aren't both set
      exportConfirm: { show: false, kind: null, label: "", eta: 0 },
      // Sejarah Semak Semula
      historyDialog: false,
      historyRows: [],
      historyLoading: false,
      errorDialog: { show: false, job: null },
      snackbar: { show: false, message: "", color: "success", timeout: 5000 },
      hidSearch: "",
      hidTimer: null,
      // The combined "Tahan & Semak" toggle value — a comma list the API reads as
      // whereIn(status, [...]). Kept as a constant so the template and the export
      // filename logic reference the same string.
      COMBINED: "TAHAN,SEMAK",
      // Kod Kuiri mini-popup (opened from an expanded HID's kuiri code).
      kuiriInfo: { show: false, code: null, data: null, familyName: "" },
      STATUSES: Object.keys(STATUS_STYLE),
      options: { page: 1, itemsPerPage: 25, sortBy: [], sortDesc: [] },
      expanded: [], detail: {}, detailLoading: {},
      animatingOut: {}, collapseTimers: {},
      // Grouped into the two header rows by `headerGroups` below. BNO/ST/NOTK/
      // NOIR come from jr2 (not the results table), so they cannot be sorted
      // server-side — marked sortable:false rather than showing a dead arrow.
      listHeaders: [
        // HID needs an explicit width: it is the only column without one, so it
        // absorbed all the table's slack and stretched on wide screens. 26
        // digits at 12px ≈ 176px + padding.
        { text: "HID", value: "HID", width: 200, group: "ident" },
        { text: "NG (Negeri)", value: "NG", width: 96, align: "center", group: "ident" },
        { text: "BNO (Batch)", value: "BNO", width: 104, align: "center", sortable: false, group: "ident" },
        { text: "P (Pusingan)", value: "P", width: 108, align: "center", group: "ident" },
        { text: "ST (Strata)", value: "ST", width: 98, align: "center", sortable: false, group: "ident" },
        { text: "NOTK", value: "NOTK", width: 84, align: "center", sortable: false, group: "ident" },
        { text: "NOIR", value: "NOIR", width: 78, align: "center", sortable: false, group: "ident" },
        { text: "Bil. Kuiri Kritikal", value: "k", width: 128, align: "center", group: "kuiri" },
        { text: "Bil. Kuiri Amaran", value: "a", width: 128, align: "center", group: "kuiri" },
        { text: "Bil. Kuiri Info", value: "i", width: 112, align: "center", group: "kuiri" },
        { text: "Jumlah", value: "total", width: 86, align: "center", group: "kuiri" },
        // wider than the chip alone — it also carries the expand chevron
        { text: "Status", value: "status", width: 132, align: "center", group: "kuiri" },
      ],
      groupLabels: { ident: "Pengecam Isi Rumah (HID)", kuiri: "Ringkasan Kuiri" },
    };
  },
  watch: {
    filterKeluarga(newVal) {
      if (newVal && this.filterKodKuiri) {
        const k = this.kuiriList.find(x => x.code === this.filterKodKuiri);
        if (k && k.family !== newVal) this.filterKodKuiri = null;
      }
    }
  },
  computed: {
    negeriOptions() { return NEGERI.map((n) => ({ value: n[0], text: `${n[0]} - ${n[1]}` })); },
    // The five Buku Panduan families and nothing else — same list, same
    // "SJ — Semakan Julat" labels as the Rujukan page's familyOptions.
    keluargaOptions() {
      return this.keluargaList.map((f) => ({ value: f.code, text: `${f.code} — ${f.name}` }));
    },
    kuiriOptions() {
      let list = this.kuiriList;
      if (this.filterKeluarga) {
        list = list.filter((k) => k.family === this.filterKeluarga);
      }
      return list.map((k) => ({
        value: k.code,
        text: k.jenisRekod ? `${k.code} - ${k.jenisRekod}` : k.code,
      }));
    },
    pusinganOptions() { return PUSINGAN; },
    negeris() { const l = NEGERI.map((n) => ({ value: n[0], name: n[1] })); return this.filterNegeri ? l.filter((n) => n.value === this.filterNegeri) : l; },
    pusingans() { return this.filterPusingan ? [this.filterPusingan] : PUSINGAN; },
    // Parsed the same way the API splits `hid=` (commas and/or whitespace), so
    // the echo under the title shows exactly what the server will search for.
    hidTerms() {
      return String(this.hidSearch || "").split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
    },
    // Which of the two scope filters are still unset — drives both the decision
    // to warn and the wording of the warning itself.
    exportMissingScope() {
      const missing = [];
      if (!this.filterNegeri) missing.push("Negeri");
      if (!this.filterPusingan) missing.push("Pusingan");
      return missing;
    },
    // Pretty-printed payload for the Ralat dialog. Kept to the fields that
    // explain the failure — the whole job row would bury the message in
    // progress counters.
    errorJson() {
      const j = this.errorDialog.job;
      if (!j) return "";
      return JSON.stringify({
        id: j.id,
        status: j.status,
        skop: {
          negeri: j.filter_ng || "SEMUA",
          pusingan: j.filter_p || "SEMUA",
        },
        oleh: j.triggered_by_name || null,
        mula: j.started_at,
        tamat: j.finished_at,
        tempoh_saat: j.duration_seconds,
        batch: { selesai: j.batches_done, jumlah: j.batches_total },
        hid_selesai: j.hids_done,
        // Split form first — it's the readable one when many batches failed —
        // with the untouched string kept alongside for copy/paste into a report.
        ralat: j.error_parts && j.error_parts.length > 1 ? j.error_parts : j.error_message,
        ralat_penuh: j.error_message,
      }, null, 2);
    },
    runningExports() {
      return Object.keys(this.exportJobs).map((k) => Object.assign({ kind: k }, this.exportJobs[k]));
    },
    dataHeaders() { return this.listHeaders; },
    // Pads a PARTIALLY filled page out to itemsPerPage with blank rows, so a
    // search matching 3 of 25 still draws a full grid instead of 3 lonely rows.
    // Only covers the >0 results case — when the search matches nothing Vuetify
    // never renders rows at all (see bodyMinHeight).
    displayRows() {
      const rows = this.listRows;
      const per = this.options.itemsPerPage;
      if (!per || per <= 0 || rows.length >= per) return rows;
      const pad = [];
      for (let i = rows.length; i < per; i++) pad.push({ __pad: true, HID: `__pad_${i}` });
      return rows.concat(pad);
    },
    // Height to hold the tbody at, in px: one dense row is ~29px (12px text +
    // 5px padding top/bottom + 1px border). Rounded UP to 30 deliberately — the
    // reserved height then always meets or exceeds what the rows actually need,
    // so the body is the SAME height whether the search returned 0, 3 or a full
    // page, and the page never reflows under the user.
    bodyMinHeight() {
      const per = this.options.itemsPerPage;
      if (!per || per <= 0) return 0;
      return per * 30;
    },
    // Consecutive columns sharing a `group` collapse into one spanning cell.
    headerGroups() {
      const out = [];
      this.listHeaders.forEach((h) => {
        const last = out[out.length - 1];
        if (last && last.key === h.group) last.span++;
        else out.push({ key: h.group, text: this.groupLabels[h.group] || h.group, span: 1 });
      });
      return out;
    },
    // data columns + the leading "#" (the chevron shares the Status cell)
    totalCols() { return this.listHeaders.length + 1; },
    sortCol() { return (this.options.sortBy || [])[0] || null; },
    sortDesc() { return !!(this.options.sortDesc || [])[0]; },
    // The table is server-paginated, so sorting has to go to the API — without
    // this the header arrows would toggle and change nothing.
    sortParams() {
      const by = (this.options.sortBy || [])[0];
      if (!by) return {};
      return { sortBy: by, sortDesc: !!(this.options.sortDesc || [])[0] };
    },
    doneByPusingan() {
      const out = {};
      this.pusingans.forEach((p) => { out[p] = this.negeris.filter((ng) => { const c = this.cell(ng.value, p); return c && c.allClean; }).length; });
      return out;
    },
    // Server-computed (HID-based when available, else batches). Falls back to
    // the batch ratio for jobs from before the API returned `percent`.
    jobPct() {
      if (!this.job) return 0;
      if (this.job.percent != null) return this.job.percent;
      return this.job.batches_total ? Math.round((this.job.batches_done / this.job.batches_total) * 100) : 0;
    },
    // Wall-clock time the run is expected to finish, from the ETA the server
    // sent on the last poll (so it stays right even if the clocks differ).
    etaClock() {
      if (!this.job || this.job.eta_seconds == null) return "";
      const t = new Date(Date.now() + this.job.eta_seconds * 1000);
      return t.toLocaleTimeString("ms-MY", { hour: "2-digit", minute: "2-digit" });
    },
  },
  mounted() { this.loadKeluarga(); this.loadKuiriList(); this.loadSummary(); this.pollStatus(); },
  beforeDestroy() { if (this.pollTimer) clearInterval(this.pollTimer); },
  methods: {
    resetFilters() {
      this.filterNegeri = null;
      this.filterPusingan = null;
      this.filterKeluarga = null;
      this.filterKodKuiri = null;
      this.statusFilter = "";
      this.hidSearch = "";
      this.options.page = 1;
      this.onFilter();
    },
    cell(ng, p) { return this.cells[`${ng}|${p}`]; },
    rowNo(index) { return (this.options.page - 1) * this.options.itemsPerPage + index + 1; },
    /**
     * Excel / PDF are generated on the SERVER (/export-excel, /export-pdf).
     * The kuiri are recomputed server-side anyway, so building the file there
     * avoids shipping ~10 MB of JSON and ~34 paged round-trips just to rebuild
     * the same rows in the browser. Here we only trigger the download.
     */
    async downloadReport(kind) {
      const isPdf = kind === "pdf";
      const flag = isPdf ? "exportingPdf" : "exporting";
      if (this[flag]) return;              // ignore a second click on the same button
      this[flag] = true;
      // Per-format job entry. Excel and PDF can run at once, and a single shared
      // progress string made them overwrite each other's text and clear it early.
      this.$set(this.exportJobs, kind, {
        label: isPdf ? "PDF" : "Excel",
        eta: this.estimateExportSeconds(isPdf),
        elapsed: 0,
      });
      const tick = setInterval(() => {
        const j = this.exportJobs[kind];
        if (j) this.$set(this.exportJobs, kind, Object.assign({}, j, { elapsed: j.elapsed + 1 }));
      }, 1000);
      try {
        const path = kind === "pdf" ? "/modul-validasi/export-pdf" : "/modul-validasi/export-excel";
        const res = await axios.get(path, {
          params: this.exportParams(),
          responseType: "blob",
        });
        const type = kind === "pdf" ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        const url = window.URL.createObjectURL(new Blob([res.data], { type }));
        const a = document.createElement("a");
        a.href = url;
        a.download = this.exportFileBase() + (kind === "pdf" ? ".pdf" : ".xlsx");
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        // an error response arrives as a Blob — read it back for the real reason
        let msg = kind === "pdf" ? "Eksport PDF gagal." : "Eksport Excel gagal.";
        try {
          const body = e.response && e.response.data;
          if (body && typeof body.text === "function") {
            const parsed = JSON.parse(await body.text());
            if (parsed && parsed.message) msg = parsed.message;
          }
        } catch (_) { /* keep the default message */ }
        this.notify(msg, "error");
      } finally {
        clearInterval(tick);
        this[flag] = false;
        this.$delete(this.exportJobs, kind);
      }
    },
    exportExcel() { return this.requestExport("excel"); },
    exportPDF() { return this.requestExport("pdf"); },
    /**
     * Gate in front of downloadReport: with Negeri or Pusingan unset the export
     * covers the whole year, so confirm first. With both set it starts straight
     * away — the common case stays one click.
     */
    requestExport(kind) {
      if (!this.exportMissingScope.length) return this.downloadReport(kind);
      this.exportConfirm = {
        show: true,
        kind,
        label: kind === "pdf" ? "PDF" : "Excel",
        eta: this.estimateExportSeconds(kind === "pdf"),
      };
    },
    confirmExport() {
      const kind = this.exportConfirm.kind;
      this.exportConfirm = Object.assign({}, this.exportConfirm, { show: false });
      if (kind) this.downloadReport(kind);
    },
    /**
     * Rough wall-clock estimate, from measured throughput: the cost is dominated
     * by recomputing each HID's kuiri server-side (~4.5ms/row), plus a couple of
     * seconds of fixed overhead; the PDF then spends a little longer laying out
     * pages. Deliberately generous — an estimate that runs out early is worse
     * than one that finishes sooner than promised.
     */
    estimateExportSeconds(isPdf) {
      const rows = this.listTotal || 0;
      return Math.max(3, Math.round(2 + rows * (isPdf ? 0.0050 : 0.0045)));
    },
    fmtClock(sec) {
      const s = Math.max(0, Math.round(sec));
      if (s < 60) return `${s}s`;
      return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;
    },

    // Debounced so typing a 26-digit HID does not fire a request per keystroke.
    onHidInput() {
      clearTimeout(this.hidTimer);
      this.hidTimer = setTimeout(() => {
        this.options.page = 1;
        this.expanded = [];
        this.animatingOut = {};
        this.loadList();
      }, 400);
    },
    onHidClear() {
      clearTimeout(this.hidTimer);
      this.hidSearch = "";
      this.options.page = 1;
      this.expanded = [];
      this.animatingOut = {};
      this.loadList();
    },
    isRowExpanded(item) {
      if (!item || !item.HID) return false;
      return this.expanded.some((x) => x.HID === item.HID) && !this.animatingOut[item.HID];
    },
    toggleExpand(item) {
      if (!item || item.__pad) return;
      const isOpen = this.isRowExpanded(item);
      if (isOpen) {
        this.$set(this.animatingOut, item.HID, true);
        const timer = setTimeout(() => {
          this.expanded = this.expanded.filter((x) => x.HID !== item.HID);
          this.$delete(this.animatingOut, item.HID);
          delete this.collapseTimers[item.HID];
        }, 300);
        if (!this.collapseTimers) this.collapseTimers = {};
        if (this.collapseTimers[item.HID]) clearTimeout(this.collapseTimers[item.HID]);
        this.collapseTimers[item.HID] = timer;
      } else {
        if (this.collapseTimers && this.collapseTimers[item.HID]) {
          clearTimeout(this.collapseTimers[item.HID]);
          delete this.collapseTimers[item.HID];
        }
        if (!this.detail[item.HID]) {
          this.$set(this.detailLoading, item.HID, true);
        }
        this.$delete(this.animatingOut, item.HID);

        const prevItems = [...this.expanded];
        prevItems.forEach((prev) => {
          if (prev.HID !== item.HID) {
            this.$set(this.animatingOut, prev.HID, true);
            const prevTimer = setTimeout(() => {
              this.expanded = this.expanded.filter((x) => x.HID !== prev.HID);
              this.$delete(this.animatingOut, prev.HID);
            }, 300);
            if (this.collapseTimers[prev.HID]) clearTimeout(this.collapseTimers[prev.HID]);
            this.collapseTimers[prev.HID] = prevTimer;
          }
        });

        if (!this.expanded.some((x) => x.HID === item.HID)) {
          this.expanded.push(item);
        }
        this.loadDetail({ item, value: true });
      }
    },
    // Whole-row toggle, except when the user was selecting text — dragging to
    // copy a HID ends in a click on the row, which would otherwise expand it.
    onRowClick(item) {
      const sel = window.getSelection && window.getSelection();
      if (sel && String(sel).length) return;
      this.toggleExpand(item);
    },
    // asc -> desc -> unsorted. Writing `options` fires update:options -> loadList.
    toggleSort(h) {
      if (h.sortable === false) return;
      let sortBy = [h.value];
      let sortDesc = [false];
      if (this.sortCol === h.value) {
        if (!this.sortDesc) sortDesc = [true];
        else { sortBy = []; sortDesc = []; }
      }
      this.options = Object.assign({}, this.options, { sortBy, sortDesc, page: 1 });
    },
    onFilter() { this.options.page = 1; this.expanded = []; this.animatingOut = {}; this.loadSummary(); this.loadList(); },
    // Its own endpoint rather than /modul-validasi/kuiri (which the Rujukan page
    // uses) — that one ships all 496 kuiri just to name five families.
    async loadKeluarga() {
      try {
        const { data } = await axios.get(`/modul-validasi/keluarga`);
        this.keluargaList = (data.data && data.data.list) || [];
      } catch (e) { /* dropdown just stays empty */ }
    },
    async loadSummary() {
      this.loading = true;
      try {
        const params = {};
        if (this.filterNegeri) params.NG = this.filterNegeri;
        if (this.filterPusingan) params.P = this.filterPusingan;
        const { data } = await axios.get(`/modul-validasi/summary`, { params });
        this.needsSchema = !!data.data.needsSchema;
        const map = {}; (data.data.cells || []).forEach((c) => { map[`${c.NG}|${c.P}`] = c; });
        this.cells = map;
      } catch (e) { /* ignore */ } finally { this.loading = false; }
    },
    async loadList() {
      this.listLoading = true;
      try {
        const params = { page: this.options.page, perPage: this.options.itemsPerPage, ...this.sortParams };
        if (this.filterNegeri) params.NG = this.filterNegeri;
        if (this.filterPusingan) params.P = this.filterPusingan;
        if (this.filterKeluarga) params.keluarga = this.filterKeluarga;
        if (this.filterKodKuiri) params.kod = this.filterKodKuiri;
        if (this.statusFilter) params.status = this.statusFilter;
        if (this.hidSearch && this.hidSearch.trim()) params.hid = this.hidSearch.trim();
        const { data } = await axios.get(`/modul-validasi/list`, { params });
        this.listRows = data.data.rows;
        this.listTotal = data.data.total;
        this.staleKeluarga = data.data.staleKeluarga || 0;
        if (data.data.counts) this.listCounts = data.data.counts;
        this.needsSchema = this.needsSchema || !!data.data.needsSchema;
      } catch (e) { this.listRows = []; this.listTotal = 0; } finally { this.listLoading = false; }
    },
    async loadKuiriList() {
      try {
        const { data } = await axios.get('/modul-validasi/kuiri');
        this.kuiriList = (data.data && data.data.kuiri) || [];
      } catch (e) { /* ignore */ }
    },
    notify(message, color = "success") {
      this.snackbar = { show: true, message, color, timeout: 6000 };
    },
    // Every on-screen filter goes into the export, HID search and sort included.
    exportParams() {
      const params = { ...this.sortParams };
      if (this.filterNegeri) params.NG = this.filterNegeri;
      if (this.filterPusingan) params.P = this.filterPusingan;
      if (this.filterKeluarga) params.keluarga = this.filterKeluarga;
      if (this.filterKodKuiri) params.kod = this.filterKodKuiri;
      if (this.statusFilter) params.status = this.statusFilter;
      if (this.hidSearch && this.hidSearch.trim()) params.hid = this.hidSearch.trim();
      return params;
    },
    // Mirrors fileBase() in the API's exportReport.js — this one wins for the
    // browser download (blob + a.download), that one for direct API calls, so
    // both end with the same _YYYYMMDD stamp.
    exportFileBase() {
      const bits = ["senarai-hid"];
      if (this.filterNegeri) bits.push(`NG${this.filterNegeri}`);
      if (this.filterPusingan) bits.push(`P${this.filterPusingan}`);
      if (this.filterKeluarga) bits.push(this.filterKeluarga);
      if (this.filterKodKuiri) bits.push(this.filterKodKuiri);
      if (this.statusFilter) {
        bits.push(this.statusFilter.split(",").map((s) => this.statusLabel(s.trim()).toUpperCase()).join("-"));
      }
      if (this.hidTerms.length) bits.push(`carian${this.hidTerms.length}`);
      bits.push(fileStamp());
      return bits.join("_");
    },
    async loadDetail({ item, value }) {
      if (!value) return;
      if (!this.detailLoading[item.HID]) {
        this.$set(this.detailLoading, item.HID, true);
      }
      const startTime = Date.now();
      try {
        const { data } = await axios.post(`/modul-validasi/run`, { HID: item.HID });
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, 280 - elapsed);
        if (remaining > 0) {
          await new Promise((resolve) => setTimeout(resolve, remaining));
        }
        this.$set(this.detail, item.HID, data.data);
      } catch (e) { /* ignore */ } finally {
        this.$set(this.detailLoading, item.HID, false);
      }
    },
    openHistory() {
      this.historyDialog = true;
      this.loadHistory();
    },
    async loadHistory() {
      this.historyLoading = true;
      try {
        const { data } = await axios.get(`/modul-validasi/recheck/history`, { params: { limit: 50 } });
        this.historyRows = (data.data && data.data.rows) || [];
      } catch (e) {
        this.historyRows = [];
        this.notify("Gagal memuatkan sejarah semakan.", "error");
      } finally { this.historyLoading = false; }
    },
    showJobError(job) { this.errorDialog = { show: true, job }; },
    async copyErrorJson() {
      try {
        await navigator.clipboard.writeText(this.errorJson);
        this.notify("JSON ralat disalin.");
      } catch (e) { this.notify("Gagal menyalin JSON.", "error"); }
    },
    // "01" -> "01 - Johor"; unknown codes fall back to the bare code.
    negeriLabel(code) {
      const hit = NEGERI.find((n) => n[0] === code);
      return hit ? `${hit[0]} - ${hit[1]}` : code;
    },
    jobStatusLabel(s) {
      return { running: "Berjalan", completed: "Berjaya", failed: "Gagal" }[s] || s;
    },
    jobStatusStyle(s) {
      return {
        running: { bg: "blue lighten-4", text: "blue--text text--darken-4" },
        completed: { bg: "green lighten-4", text: "green--text text--darken-4" },
        failed: { bg: "red lighten-4", text: "red--text text--darken-4" },
      }[s] || { bg: "grey lighten-3", text: "grey--text text--darken-2" };
    },
    async recheck(scope) {
      const body = {};
      if (scope === "negeri" || scope === "batch") body.NG = this.filterNegeri;
      if (scope === "pusingan" || scope === "batch") body.P = this.filterPusingan;
      try { await axios.post(`/modul-validasi/recheck`, body); this.pollStatus(true); }
      catch (e) { const m = e.response && e.response.data && e.response.data.message; this.$nextTick(() => alert(m || "Gagal memulakan semakan")); }
    },
    pollStatus(start) {
      const tick = async () => {
        try {
          const { data } = await axios.get(`/modul-validasi/recheck/status`);
          const prev = this.job && this.job.status;
          this.job = data.data;
          if (this.job && this.job.status !== "running") {
            if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
            if (prev === "running") { this.loadSummary(); this.loadList(); }
          }
        } catch (e) { /* ignore */ }
      };
      tick();
      if ((start || (this.job && this.job.status === "running")) && !this.pollTimer) this.pollTimer = setInterval(tick, 2500);
    },
    fmtNum(n) { return Number(n || 0).toLocaleString("en-US"); },
    fmtDate(d) {
      if (!d) return "—";
      try {
        return new Date(d).toLocaleString("ms-MY", {
          day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
        });
      } catch (e) { return String(d); }
    },
    // Compact duration: "45 saat" / "18 minit" / "1 jam 5 minit".
    fmtDur(sec) {
      const s = Math.max(0, Math.round(sec || 0));
      if (s < 60) return `${s} saat`;
      const m = Math.round(s / 60);
      if (m < 60) return `${m} minit`;
      const h = Math.floor(m / 60);
      const rm = m % 60;
      return rm ? `${h} jam ${rm} minit` : `${h} jam`;
    },
    sevColor(s) { return s === "K" ? "red darken-1" : s === "A" ? "orange darken-2" : "blue darken-1"; },
    sevLabel(s) { return s === "A" ? "Amaran" : s === "I" ? "Info" : "Kritikal"; },
    // Kod Kuiri popup — reuses the catalogue already loaded for the Kod Kuiri
    // filter (kuiriList from /modul-validasi/kuiri), so no extra request.
    openKuiriInfo(code) {
      const data = this.kuiriList.find((k) => k.code === code) || null;
      const fam = data && this.keluargaList.find((f) => f.code === data.family);
      this.kuiriInfo = { show: true, code, data, familyName: fam ? fam.name : "" };
    },
    // Shared by the filter toggle and the Status chips — see STATUS_STYLE.
    statusStyle(st) { return STATUS_STYLE[st] || { bg: "grey lighten-3", text: "grey--text text--darken-2" }; },
    // Display wording. The engine/DB value stays "BOLEH HANTAR" (it is what the
    // API filters and orders on) — only the label shown to users is "Lulus".
    statusLabel(st) { return { TAHAN: "Tahan", SEMAK: "Semak", "BOLEH HANTAR": "Lulus" }[st] || st; },
    statusTooltip(st) {
      return {
        TAHAN: "Mempunyai sekurang-kurangnya 1 ralat Kritikal (K). Penghantaran data disekat.",
        SEMAK: "Mempunyai ralat Amaran (A) sahaja (tiada Kritikal). Memerlukan semakan semula.",
        "BOLEH HANTAR": "Tiada ralat Kritikal atau Amaran. Rekod bersih dan boleh dihantar.",
      }[st] || "Paparkan semua rekod HID tanpa mengira status.";
    },
  },
};
</script>

<style scoped>
/* Keluarga select — deliberately left at Vuetify's stock `outlined dense`
   metrics so it reads as the SAME control as the Pusingan/Negeri selects in the
   card above: no font-size, min-height or width overrides here. It now sits in
   a v-col of the filter bar, so the grid sizes it. */
/* A family name longer than the column would otherwise spill over the outline
   instead of being clipped — the select's own box has no ellipsis by default. */
.keluarga-select >>> .v-select__selection--comma {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ---- status toggle: label on top, count underneath ----
   Selected state uses OUTLINE, not border: Vuetify pins the toggle's
   border-color with an !important rule of its own, and fighting it needs an
   ever-growing selector. Nothing competes for outline, so the highlight always
   wins. Doubled `.v-btn.v-btn` matches Vuetify's own specificity trick. */
.status-toggle >>> .v-btn.v-btn {
  height: 44px !important;
  flex-direction: column;
  padding: 0 12px !important;
  opacity: 0.5 !important;
  filter: grayscale(0.4);
  transition: opacity 0.2s ease, filter 0.2s ease;
}
.status-toggle >>> .v-btn.v-btn.v-btn--active {
  opacity: 1 !important;
  filter: none;
  outline: 2px solid #37474f;
  outline-offset: -2px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.22) !important;
  z-index: 1;
}
.status-toggle >>> .v-btn.v-btn.v-btn--active .tg-label {
  font-weight: 700;
  color: #263238;
}
/* Vuetify's overlay tint washes the fill out when active — drop it so the
   button keeps exactly the colour that matches its Status chip. */
.status-toggle >>> .v-btn.v-btn--active::before { opacity: 0 !important; }
.status-toggle >>> .v-btn__content {
  flex-direction: column;
  line-height: 1.1;
}
.tg-label { font-size: 11px; letter-spacing: 0.02em; }
.tg-count { font-size: 13px; font-weight: 700; }

/* ---- Negeri × Pusingan cell tooltip ---- */
.cell-hit { display: inline-block; cursor: help; }
.cell-tip { font-size: 12px; line-height: 1.45; min-width: 168px; }
.cell-tip__head {
  font-weight: 700;
  border-bottom: 1px solid rgba(255, 255, 255, 0.25);
  padding-bottom: 3px;
  margin-bottom: 4px;
  white-space: nowrap;
}
.cell-tip table { width: 100%; border-collapse: collapse; }
.cell-tip td { padding: 0; }
.cell-tip td:last-child { text-align: right; font-weight: 600; padding-left: 14px; }
.cell-tip__foot {
  margin-top: 4px;
  padding-top: 3px;
  border-top: 1px solid rgba(255, 255, 255, 0.25);
  font-size: 11px;
  opacity: 0.85;
  white-space: nowrap;
}

/* ---- Sejarah Semak Semula ---- */
.hist-table >>> th {
  font-size: 11px !important;
  color: #546e7a !important;
  white-space: nowrap;
  background: #eceff1;
  position: sticky;
  top: 0;
  z-index: 1;
}
.hist-table >>> td {
  font-size: 12px !important;
  white-space: nowrap;
}
/* Raw error payload. Wrapping is on: these strings are single-line SQL
   thousands of characters long, which would otherwise scroll sideways
   indefinitely instead of being readable. */
.err-json {
  margin: 0;
  padding: 14px 16px;
  font-family: Consolas, Monaco, "Courier New", monospace;
  font-size: 11.5px;
  line-height: 1.5;
  color: #263238;
  background: #f7f9fa;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

/* bottom expand/collapse hint under the summary matrix */
.summary-expand-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px 0;
  font-size: 12px;
  font-weight: 500;
  color: #1976d2;
  cursor: pointer;
  border-top: 1px solid #eee;
  background: #fafafa;
  user-select: none;
}
.summary-expand-hint:hover { background: #eef4fb; }

/* running-export status (Excel and PDF can run concurrently) */
.export-status { font-size: 12px; line-height: 1.3; min-width: 200px; }
.export-job {
  font-size: 11.5px;
  line-height: 1.2;
  white-space: nowrap;
  margin-top: 3px;
}

/* echo of the in-header HID search, under the "Senarai HID" title */
.hid-echo {
  font-size: 12px;
  font-weight: 400;
  line-height: 1.3;
  color: #546e7a;
  max-width: 620px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hid-echo b { color: #1976d2; font-weight: 600; }
.hid-echo-clear { cursor: pointer; vertical-align: baseline; }
.hid-echo-clear:hover { color: #d32f2f !important; }

/* ---- Senarai HID table — same look as Data Collapse HES / the Pamer Data
       "Eksport CSV" preview, minus the frozen columns (none float here). ---- */
.hid-table >>> .v-data-table__wrapper {
  /* No vertical cap and no own scrollbar: the table grows/shrinks with its
     rows and the page scrolls past it, instead of trapping the user inside a
     small fixed-height scrollbox. Horizontal scroll is kept — the column set
     is wide and shouldn't force the whole page to scroll sideways. */
  overflow-x: auto;
  /* Reserve a full page of rows + the two header rows (30px each). Keeping the
     box this tall even when the search matches nothing is what stops the page
     from scrolling itself back to the top. */
  min-height: calc(var(--hid-body-min, 0px) + 60px);
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #fff;
}
.hid-table >>> .v-data-table__wrapper > table {
  border-collapse: separate;
  border-spacing: 0;
  white-space: nowrap;
  min-width: 100%;
}
.hid-table >>> .v-data-table__wrapper > table th,
.hid-table >>> .v-data-table__wrapper > table td {
  padding: 5px 10px !important;
  height: auto !important;
  font-size: 12px !important;
  border-bottom: 1px solid #eee;
  border-right: 1px solid #f2f2f2;
}
.hid-table >>> .v-data-table__wrapper > table thead th {
  position: sticky;
  z-index: 2;
  background: #eceff1;
  font-weight: 600;
  color: #37474f !important;
  border-bottom: 2px solid #cfd8dc !important;
}
/* Two sticky header rows: the group row on top, the column row docked directly
   under it. The group row gets a FIXED height so the second row's offset is
   exact — measured row heights are fractional (29.6px), which would leave a
   sub-pixel gap for scrolled content to show through. */
.hid-table >>> .v-data-table__wrapper > table thead tr:first-child th {
  top: 0;
  /* Must out-specify the `height: auto !important` on th/td above, hence the
     full deep selector rather than a bare .grp-col rule. */
  height: 30px !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}
.hid-table >>> .v-data-table__wrapper > table thead tr:nth-child(2) th { top: 30px; }
.grp-col {
  background: #dde5e9 !important;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  font-size: 11px !important;
  border-right: 2px solid #cfd8dc !important;
}
/* "Pengecam Isi Rumah (HID)" reads as one block: the group cell, all seven of
   its column headers, and the leading "#" share one tint. */
.hid-table >>> thead th.ident-col { background: #d6e4f0 !important; }
.hid-table >>> thead th.sub-col.ident-col:hover { background: #c4d8ea !important; }
.sub-col { user-select: none; }
/* In-header HID search — inline-flex so it sits on the same line as the "HID"
   label; v-input is display:flex/width:100% by default, which pushed it onto a
   second line and made the whole header row taller. Kept short enough that the
   row height is unchanged. */
.hid-search {
  display: inline-flex !important;
  flex: 0 0 auto !important;
  width: 122px;
  margin-left: 6px;
  vertical-align: middle;
}
.hid-search >>> .v-input__control,
.hid-search >>> .v-input__slot { min-height: 20px !important; }
.hid-search >>> .v-input__slot {
  padding: 0 4px !important;
  background: #fff !important;
  border: 1px solid #b8cadb !important;
  border-radius: 3px;
  box-shadow: none !important;
}
.hid-search >>> input {
  font-size: 11px;
  font-weight: 400;
  padding: 0;
  height: 18px;
}
.hid-search >>> .v-input__append-inner { margin-top: 1px !important; padding-left: 0 !important; }
.hid-search >>> .v-icon { font-size: 13px; }
.sub-col.sortable { cursor: pointer; }
.sub-col.sortable:hover { background: #dfe6ea !important; }
.sub-col.active { color: #1976d2 !important; }
/* the arrow only takes up space when the column is the active sort */
.sort-ico { opacity: 0; transition: opacity 0.15s; }
.sub-col.sortable:hover .sort-ico { opacity: 0.45; }
.sort-ico.show { opacity: 1; color: #1976d2 !important; }

.hid-table >>> .v-data-table__wrapper > table tbody tr { background: #fff; }
.hid-table >>> .v-data-table__wrapper > table tbody tr:nth-child(even) { background: #fafafa; }
.hid-table >>> .v-data-table__wrapper > table tbody tr:hover { background: #e3f2fd !important; }
.idx-col {
  color: #90a4ae;
  text-align: right;
  width: 56px;
  min-width: 56px;
}
.pk-col {
  background: #fff8e1;
  font-weight: 600;
}
/* the "#" header is part of the identity block — its tint comes from
   th.ident-col above; no grey override here (it would win on source order) */
/* status chip + expand chevron share one cell */
.status-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}
.exp-btn { margin-right: -4px; }
.hid-table >>> .empty-cell { color: #bbb; }

/* rows are clickable (the whole row toggles the kuiri panel) */
.hid-table >>> tbody tr.hid-row { cursor: pointer; }
.hid-table >>> tbody tr.hid-row.is-open { background: #e3f2fd !important; }
/* padding rows (see displayRows): blank filler, never interactive */
.hid-table >>> tbody tr.hid-row--pad { cursor: default; }
.hid-table >>> tbody tr.hid-row--pad:hover { background: inherit !important; }

/* Expanded kuiri panel — inset so it reads as belonging to the row above.
   CRITICAL: the panel must never widen the outer table. Its <td> spans every
   column, so any content wider than their sum stretches the whole table (it
   grew when the nav drawer opened and squeezed the page). `table-layout: fixed`
   + `width: 100%` makes the inner table take exactly the width on offer, and
   wrapping is re-enabled so long Mesej/Penyelesaian text flows down, not out. */
.hid-table >>> tbody tr.kuiri-row,
.hid-table >>> tbody tr.kuiri-row:hover { background: #f5f7f8 !important; }
.hid-table >>> tbody tr.kuiri-row > td {
  border-bottom: 2px solid #cfd8dc;
  /* Overrides the outer table's `white-space: nowrap`. THIS is what stops the
     panel widening the table: with the text allowed to wrap (plus the
     overflow-wrap below) the cell's min-content width collapses, so the
     colspan cell no longer demands a huge width.
     Do NOT add `max-width: 0` here — it makes the inner table's `width: 100%`
     resolve against zero, collapsing Mesej to ~30px and wrapping it into a
     900px-tall column of single characters. */
  white-space: normal;
}
/* The wrapper/table/header rules above use deep selectors, so they match ANY
   descendant — including the v-simple-table nested inside an expanded panel.
   Reset what leaks in. Most importantly `--hid-body-min`: the nested wrapper
   inherited the full-page-height floor, which padded every expanded panel out
   to ~810px of white space regardless of how few kuiri it held. */
.hid-table >>> .kuiri-table .v-data-table__wrapper {
  min-height: 0;
  overflow: visible;
  border: 0;
  border-radius: 0;
  background: transparent;
}
/* the panel's own header is not the table header — drop the sticky grey band */
/* !important: the outer `... .v-data-table__wrapper > table thead th` rule is the
   more specific selector, so it wins on position/z-index without this. Left
   sticky, the panel header would detach and float as the page scrolls (the
   nested wrapper no longer scrolls itself). */
.hid-table >>> .kuiri-table thead th {
  position: static !important;
  z-index: auto !important;
  background: transparent !important;
  border-bottom: 1px solid #dbe2e6 !important;
}
.kuiri-table >>> table {
  background: transparent;
  table-layout: fixed;
  width: 100%;
}
.kuiri-table >>> th,
.kuiri-table >>> td {
  white-space: normal;
  overflow-wrap: anywhere;
}
.kuiri-table >>> th { font-size: 11px !important; color: #546e7a !important; }
/* NOAIR hint icon in the "Ahli Rumah" header — a plain glyph, no disc or
   border. `cursor: help` marks it as explanatory rather than clickable: it
   opens no menu, only a tooltip. */
.noair-badge {
  display: inline-flex;
  margin-left: 3px;
  cursor: help;
  vertical-align: middle;
}
.noair-badge .v-icon { color: #546e7a !important; font-size: 13px !important; }
.kuiri-table >>> td { font-size: 12px !important; vertical-align: top; }

/* --- TEAL THEME OVERRIDES --- */
.filter-bar {
  background: #f7fcfb;
  border-top: 1px solid #dbeeeb;
  border-bottom: 1px solid #dbeeeb;
}
.modulvalidasi-page .fld__label {
  font-size: var(--fs-3xs, 12px);
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #7aa8a2;
  line-height: 1.4;
  margin-bottom: 5px;
  white-space: nowrap;
}
.modulvalidasi-page .filter-bar .v-input fieldset { border-color: #cfeae7; }
.modulvalidasi-page .filter-bar .v-input--is-focused fieldset {
  border-color: #037769 !important;
  border-width: 1px;
}
.kad__tajuk { border-bottom: 1px solid #dbeeeb; }
.kad__tajuk .v-icon { color: #037769; }
/* …except on the coloured export buttons, where teal on green/red is unreadable.
   Left alone while disabled so the icon stays greyed with its label. */
.kad__tajuk .export-btns .v-btn:not(.v-btn--disabled) .v-icon { color: #fff; }
.btn-teal.v-btn {
  background: #037769 !important;
  color: #ffffff !important;
  letter-spacing: normal;
}
.btn-teal.v-btn:hover { background: #026254 !important; }
.btn-teal.v-btn .v-icon { color: #ffffff !important; }
.btn-teal.v-btn--disabled {
  background: #cfd8dc !important;
  color: rgba(0, 0, 0, .38) !important;
}
.btn-teal.v-btn--disabled .v-icon { color: rgba(0, 0, 0, .38) !important; }
.reset-btn.v-btn--outlined {
  border-color: #cfeae7;
  color: #037769;
  letter-spacing: normal;
}
.reset-btn.v-btn--outlined:hover {
  background: #eef8f6;
  border-color: #037769;
}
.reset-btn.v-btn--outlined .v-icon { color: #037769; }

/* Chevron rotation animation */
.chevron-anim {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}
.chevron-anim.rotate-180 {
  transform: rotate(180deg);
}

/* Status Validasi expandable summary matrix table */
.summary-table-head >>> table,
.summary-table-body >>> table {
  table-layout: fixed;
  width: 100%;
}
.summary-table-head >>> th:first-child,
.summary-table-head >>> td:first-child,
.summary-table-body >>> td:first-child {
  width: 200px;
  min-width: 160px;
}
.summary-table-body-wrapper {
  overflow: hidden;
}
.summary-expand-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 16px;
  border-top: 1px solid #e0e0e0;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #037769;
  user-select: none;
  background: #fafafa;
  transition: background 0.2s ease, color 0.2s ease;
}
.summary-expand-hint:hover {
  background: #eef8f6;
  color: #026254;
}

/* Expanded kuiri panel inner wrapper */
.kuiri-expand-wrapper {
  overflow: hidden;
  will-change: height;
  transform: translateZ(0);
}

/* Affected-variable read-out under each kuiri message (item 5). Set apart from
   the message prose above it: smaller, mono values, a faint top rule. */
.kuiri-values {
  margin-top: 4px;
  padding-top: 3px;
  border-top: 1px dashed #d5e3e0;
  font-size: 11px;
  line-height: 1.5;
  color: #37474f;
  font-family: "Roboto Mono", ui-monospace, Consolas, monospace;
  overflow-wrap: anywhere;
}
.kuiri-values__lbl {
  font-family: inherit;
  font-weight: 700;
  color: #037769;
  margin-right: 2px;
}

/* Kod Kuiri link inside the detail panel — opens the mini popup. */
.kod-link {
  color: #1976d2;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  border-bottom: 1px dotted #90b8de;
}
.kod-link:hover { color: #0d47a1; border-bottom-style: solid; }

/* Combined "Tahan & Semak" toggle — a red→orange sliver on its left edge so it
   reads as spanning both status colours, without a whole new palette. */
.status-toggle >>> .v-btn.v-btn.tg-combo {
  border-left: 3px solid #e64a19;
}
.status-toggle >>> .v-btn.v-btn.tg-combo.v-btn--active .tg-label { color: #b71c1c; }

/* Kod Kuiri mini popup */
.kuiri-info__title { border-bottom: 1px solid #dbeeeb; }
.kuiri-info__code {
  font-family: "Roboto Mono", ui-monospace, Consolas, monospace;
  font-weight: 600;
}
.kuiri-info__table >>> th {
  width: 150px;
  text-align: left;
  vertical-align: top;
  font-size: 11px !important;
  font-weight: 700;
  color: #7aa8a2 !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  background: #f7fcfb;
  border-right: 1px solid #eef4f3;
}
.kuiri-info__table >>> td {
  font-size: 12.5px !important;
  vertical-align: top;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
  line-height: 1.5;
}
.kuiri-info__logik {
  font-family: "Roboto Mono", ui-monospace, Consolas, monospace;
  font-size: 11px;
  color: #37474f;
  overflow-wrap: anywhere;
}
</style>
